import { PixelRatio } from 'react-native';
import config from '@dkrm/config';
import { getUrlAppendedWithQueryParamSeparator, getUrlWithOverridenQueries, parseQueriesFromUrl, removeHttpProtocol } from '@dkrm/general-libs/Utils/urlUtils';
import { KNOWN_IMAGE_HOSTS } from '../constants';

/**
 * Util to transform s3 absolute URL, into URL prefixed by Media CDN
 * eg.
 *     input: http://f1-static.s3-ap-southeast-1.amazonaws.com/catalogue/KER-7.jpg?versionId=123
 *     output: https://media.dekoruma.com/catalogue/KER-7.jpg
 */
export function getCDNImageUrl(image) {
  let url = '';
  if (!image) return url;

  // new image link doesn't need to be transformed
  if (image.includes(config.API_URL_MEDIA_CDN) || image.includes(config.API_URL_ASSET_CDN) || image.includes('localhost:8060')) return image;
  if (image.startsWith('http')) {
    url = `${config.API_URL_MEDIA_CDN}/${getS3ImageName(image)}`;
  } else {
    url = `${config.API_URL_MEDIA_CDN}/${image}`;
  }
  return url;
}

/**
 * Raw driver method for trimming S3 absolute URL to its name without path
 * @param  {string} image S3 absolute URL
 * @return {string}       image name, e.g. catalogue/KER-7.jpg
 */
// eslint-disable-next-line no-underscore-dangle
export function _getS3ImageName(_image) {
  let imageName = KNOWN_IMAGE_HOSTS.reduce((prevImage, host) => {
    const hostWithoutProtocol = removeHttpProtocol(host);
    if (prevImage.split(hostWithoutProtocol).length === 2) {
      return prevImage.split(hostWithoutProtocol).pop();
    }
    return prevImage;
  }, removeHttpProtocol(_image));
  imageName = imageName.substring(1); // remove first character === '/'

  if (imageName.indexOf('?') > -1) {
    imageName = imageName.substring(0, imageName.indexOf('?')); // remove query params ?...
  }
  return imageName;
}

/**
 * Safe driver method for _getImageName, accept S3 absolute URL or already trimmed image name
 * eg.
 *     input: http://f1-static.s3-ap-southeast-1.amazonaws.com/catalogue/KER-7.jpg
 *     output: catalogue/KER-7.jpg
 */
export function getS3ImageName(image) {
  return _getS3ImageName(getS3ImageAbsoluteUrl(image));
}

/**
 * Util to get s3 absolute URL, from imageName or s3 absolute URL itself
 * eg.
 *     input: http://f1-static.s3-ap-southeast-1.amazonaws.com/catalogue/KER-7.jpg
 *     output: catalogue/KER-7.jpg
 */
export function getS3ImageAbsoluteUrl(image) {
  let url = '';
  if (image) {
    if (image.startsWith('http')) {
      url = image;
    } else {
      url = `${config.S3_BUCKET_PUBLIC_URL}/${image}`;
    }
  }
  return url;
}

/**
 * Given the upc, get the Media CDN product url
 * @param  upc INF-1517
 * @return url https://media.dekoruma.com/catalogue/INF-1517.jpg
 */
export function getProductImageUrl(upc) {
  return `${config.API_URL_MEDIA_CDN}/catalogue/${upc}.jpg`;
}
export function getNoImageUrl() {
  return `${config.API_URL_MEDIA_CDN}/catalogue/no-img.png`;
}

/**
 * Util to grab pattern URL from s3 bucket, already cropped with specific size by {{config.API_URL_MEDIA_CDN}}.
 * With assumption that patternName equals to imageName
 * e.g.
 *     input: Teddybear
 *     output: https://media.dekoruma.com/contextual-images/teddybear.jpg?width=110&height=55&fit=crop
 */
export function getPatternImageCropUrl(patternName) {
  return `${config.API_URL_MEDIA_CDN}/contextual-images/${patternName.toLowerCase()}.jpg?width=110&height=55&fit=crop`;
}

// @deprecated (Anyone): not used anymore as of 31 Mar 2023
export function getSmallerQualityImageUrl(url, quality) {
  if (!url) return '';
  let width = 237;
  switch (quality) {
    case 'medium':
      width = 474;
      break;
    case 'large':
      width = 736;
      break;
    default:
      break;
  }
  return url.replace('https://s-media-cache-ak0.pinimg.com/originals', `https://i.pinimg.com/${width}x`).replace('https://i.pinimg.com/originals', `https://i.pinimg.com/${width}x`);
}
export function getUrlAppendedWithDimension(uri = '', w, h, type = 'bounds') {
  let currentUri = uri;
  currentUri = removeUrlParameter(currentUri, 'width');
  currentUri = removeUrlParameter(currentUri, 'height');
  currentUri = removeUrlParameter(currentUri, 'fit');
  currentUri = getUrlAppendedWithQueryParamSeparator(currentUri);
  return `${currentUri}fit=${type}${w ? `&width=${w}` : ''}${h ? `&height=${h}` : ''}`;
}
export function getUrlWithoutWhiteBackground(uri) {
  const currentUri = getUrlAppendedWithQueryParamSeparator(uri);
  return `${currentUri}trim-color=ffffff`;
}

// from https://stackoverflow.com/questions/1634748/how-can-i-delete-a-query-string-parameter-in-javascript
export function removeUrlParameter(url = '', parameter) {
  const urlparts = url.split('?');
  if (urlparts.length >= 2) {
    const prefix = `${encodeURIComponent(parameter)}=`;
    const pars = urlparts[1].split(/[&;]/g);

    // reverse iteration as may be destructive
    for (let i = pars.length; i-- > 0;) {
      // idiom for string.startsWith
      if (pars[i].lastIndexOf(prefix, 0) !== -1) {
        pars.splice(i, 1);
      }
    }
    url = urlparts[0] + (pars.length > 0 ? `?${pars.join('&')}` : '');
    return url;
  }
  return url;
}

/**
 *
 * Check whether it is s3 or Media CDN
 * @param  url http://f1-static.s3-ap-southeast-1.amazonaws.com/catalogue/KER-7.jpg
 * @param  url https://media.dekoruma.com/catalogue/KER-7.jpg
 * @return bool true
 */
export function isS3orMediaCDNImage(url) {
  if (isMediaCDNImage(url)) return true;
  return url ? url.indexOf('f1-static.s3-ap-southeast-1.amazonaws') !== -1 : false;
}
export function isInstagramImage(url) {
  const re = /http(s?):\/\/([^\.]+)\.cdninstagram\.com/g;
  return url.match(re) !== null;
}
export function isWordpressImage(url) {
  const re = /http(s?):\/\/([^\.]+)\.wp\.com/g;
  return url.match(re) !== null;
}
export function isMediaCDNImage(url) {
  const API_URL_MEDIA_CDN = removeHttpProtocol(config.API_URL_MEDIA_CDN);
  return url ? url.indexOf(API_URL_MEDIA_CDN) !== -1 : false;
}
export function getInstagramImageSize(url) {
  const re = /\/(s|p)([0-9]+)x([0-9]+)\//g;
  const result = url.match(re);
  if (result === null) {
    return null;
  }
  const wh = result[0].slice(2, result[0].length - 1).split('x').map(x => parseInt(x, 10));
  return {
    width: wh[0],
    height: wh[1]
  };
}
export function getWordpressImageSize(url) {
  const re = /\?([^=]+)=([0-9]+),([0-9]+)/g;
  const result = url.match(re);
  if (result === null) {
    return null;
  }
  const wh = result[0].split('=')[1].split(',').map(x => parseInt(x, 10));
  return {
    width: wh[0],
    height: wh[1]
  };
}
export function getMediaCDNImageSize(url) {
  const newW = /width=([0-9]+)/g;
  const newH = /height=([0-9]+)/g;
  const wh = [null, null];
  const w = url.match(newW);
  if (w !== null) wh[0] = parseInt(w[0].split('=')[1], 10);
  const h = url.match(newH);
  if (h !== null) wh[1] = parseInt(h[0].split('=')[1], 10);
  return {
    width: wh[0],
    height: wh[1]
  };
}
export function getImageSizeFromUrl(url) {
  if (!url) return null;
  if (isInstagramImage(url)) return getInstagramImageSize(url);
  if (isWordpressImage(url)) return getWordpressImageSize(url);
  if (isMediaCDNImage(url)) return getMediaCDNImageSize(url);
  return null;
}
export function resizeImage(uri, imageWidth, imageHeight, fit = 'crop') {
  if (!uri) return uri;
  return getUrlAppendedWithDimension(uri, Math.ceil(imageWidth), Math.ceil(imageHeight), fit);
}
export function getResponsiveImageUrl(uri, imageWidth, imageHeight, ratioMultiplier = 1, fit) {
  if (!uri) return uri;
  const cdnImageUrl = getCDNImageUrl(uri);

  /** handle fallback for pixel ratio returning NaN (e.g in server render) */
  const pixelRatio = PixelRatio.get();
  let ratio = (!pixelRatio || Number.isNaN(pixelRatio) ? 1 : pixelRatio) * ratioMultiplier;
  if (ratio < 1) ratio = 1;
  if (ratio > 2) ratio = 2;
  ratio = 1 + (ratio - 1) / 2;

  /** handle image quality downsizing to save bandwidth */
  // the image quality is in the range [1,100] (inclusive)
  const imageQuality = Math.min(Math.round((60 - (ratio - 1) * 15) / 10) * 10, 100);
  const resizedImageUrl = resizeImage(cdnImageUrl, imageWidth, imageHeight, fit);
  const additionalQueries = {
    optimize: 'medium',
    quality: imageQuality,
    dpr: Math.round(ratio * 10) / 10
  };
  return getUrlWithOverridenQueries(resizedImageUrl, additionalQueries);
}
export function getSSRImageUrl(uri, imageWidth = 100, imageHeight = 100, ratio = 0.8) {
  if (!uri) return null;
  let cdnImageUrl = uri;
  if (!isMediaCDNImage(uri)) cdnImageUrl = getCDNImageUrl(uri);
  const {
    fit
  } = parseQueriesFromUrl(cdnImageUrl);
  const resizedImageUrl = resizeImage(cdnImageUrl, imageWidth, imageHeight, fit);

  // zero quality to make sure the quality is overridden, but not the lowest quality
  const additionalQueries = {
    optimize: 'high',
    quality: 0,
    dpr: ratio
  };
  return getUrlWithOverridenQueries(resizedImageUrl, additionalQueries);
}
export function getFaceDetectionImageUrl(uri) {
  // Media CDN does not support face detection, so just returns the uri.
  return uri;
}
export function getSkuPartnerFromImageName(imageName) {
  return imageName.split('.').slice(0, -1).join('.').split('_')[0];
}

// @deprecated (Radit): use isS3orMediaCDNImage instead since imgix is not used anymore.
export function isS3orImgixImage(url) {
  return isS3orMediaCDNImage(url);
}

// @deprecated (Radit): use isMediaCDNImage instead since imgix is not used anymore.
export function isImgixImage(url) {
  return isMediaCDNImage(url);
}

// @deprecated (Radit): use getMediaCDNImageSize instead since imgix is not used anymore.
export function getImgixImageSize(url) {
  return getMediaCDNImageSize(url);
}

// @deprecated (Radit): remove this since Pinterest is not used anymore.
export function isPinterestImage(url) {
  return false;
}